
import {
  Component, PropSync, Ref, Prop, Watch, Mixins,
} from 'vue-property-decorator';
import { VForm } from '@/features/common/types';
import { UserInfo, UserItemType } from '@/features/users/types';
import { deleteUser } from '@/features/users/services/users.services';
import { showErrorAlertDialog } from '@/features/common/helpers';
import eventBus from '@/features/common/eventBus';
import eventNames from '@/features/common/constants/events';
import { TransferableUserType } from '@/features/users/views/Users.vue';
import focusMixin from '@/features/common/mixins/focusMixin';

@Component
export default class DeleteUserModal extends Mixins(focusMixin) {
  @Ref('deleteUsersForm') form!: VForm;

  @PropSync('show', { required: true }) syncedShow!: boolean;

  @Prop({ required: true }) user!: UserItemType;

  @Prop({ required: true }) transferToUsers!: TransferableUserType;

  private isLoading = false;

  private requiredRules = [
    (v: { fullName: string, username: string }): string | boolean => {
      return !!v.username || this.$t('labels.required') as string;
    },
  ];

  private data = {
    transferTo: {
      userId: 0,
      fullName: '',
      username: '',
    },
    transferContent: {
      addressBook: true,
      partsBook: true,
    },
  }

  private dataCloned = { ...this.data };

  get isEmailConfirmed(): boolean {
    return !!this.user.emailConfirmed;
  }

  get userInfo(): UserInfo {
    return this.$store.getters.getUserInfo;
  }

  @Watch('show')
  private resetForm(isShow: boolean) {
    if (isShow) {
      this.resetData();
    }
  }

  private async submit(): Promise<void> {
    const isValid = this.form.validate();
    if (!isValid) return;

    this.dataCloned = { ...this.data };

    if (this.isEmailConfirmed) {
      eventBus.$once(eventNames.CONFIRMATION_DIALOG, async () => {
        this.$store.commit('setLoading', true);
        await this.deleteUser();
        this.$store.commit('setLoading', false);
      });

      this.$store.commit('toggleConfirmDialog', {
        show: true,
        content: this.$t('manageUsers.deleteUser.dialog.delete.body', {
          firstName: this.user.firstName,
          fullName: this.user.fullName,
          username: this.user.username,
          transferToFullName: this.data.transferTo.fullName,
          transferToUsername: this.data.transferTo.username,
        }) as string,
        title: this.$t('manageUsers.deleteUser.dialog.delete.title', {
          fullName: this.user.fullName,
        }) as string,
      });
    } else {
      this.dataCloned.transferTo.userId = this.userInfo.userId;
      this.dataCloned.transferTo.username = this.userInfo.username;
      await this.deleteUser();
    }

    this.syncedShow = false;
    this.resetData();
  }

  private async deleteUser(): Promise<void> {
    this.isLoading = true;
    const response = await deleteUser(this.user.userId, {
      toUserId: this.dataCloned.transferTo.userId,
      transferTo: this.dataCloned.transferTo.username,
      transferFrom: this.user.username,
      transferContent: this.dataCloned.transferContent,
    });
    this.isLoading = false;

    if (!response.successful) {
      const errorMessage = response.errorMessage
        || this.$t('alertDialog.somethingWentWrong') as string;
      showErrorAlertDialog(errorMessage);
      return;
    }

    if (!response.deleted) {
      showErrorAlertDialog();
      return;
    }

    this.$emit('delete', this.user);
  }

  private async resetData() {
    this.data = {
      transferTo: {
        userId: 0,
        fullName: '',
        username: '',
      },
      transferContent: {
        addressBook: true,
        partsBook: true,
      },
    };

    await this.$nextTick();

    if (!this.$refs.deleteUsersForm) return;

    (this.$refs.deleteUsersForm as HTMLFormElement).resetValidation();
  }
}
